import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import {
  Plus,
  ArrowLeft,
  Search,
  Package,
  Boxes,
  Truck,
  CheckCircle2,
  Clock,
  MapPin,
  FileText,
  Calendar,
  Pencil,
  Trash2,
  Download
} from "lucide-react";
import { usePermissions } from "../../hooks/usePermissions";
import { handleApiError } from "../../utils/errorHandler";
import { useTranslation } from "../../contexts/I18nContext";

const UNITA_MISURA = ["pz", "kg", "m", "m2", "m3", "l"];

export default function ProduzioneMaterialePage() {
  const { t } = useTranslation();
  const { tenant } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [materiali, setMateriali] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [stabilimenti, setStabilimenti] = useState([]);
  const [aree, setAree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Filtri
  const [filtroCommessa, setFiltroCommessa] = useState(searchParams.get("commessa_id") || "__all__");
  const [filtroStato, setFiltroStato] = useState("__all_status__");
  const [filtroStabilimento, setFiltroStabilimento] = useState("");
  
  // Dialog states
  const [showDialog, setShowDialog] = useState(false);
  const [showRicezioneDialog, setShowRicezioneDialog] = useState(false);
  const [editingMateriale, setEditingMateriale] = useState(null);
  const [ricevendoMateriale, setRicevendoMateriale] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    commessa_id: "",
    sottocommessa_id: "",
    codice_marca: "",
    descrizione: "",
    quantita: 1,
    unita_misura: "pz",
    peso_kg: "",
    dimensioni: "",
    fornitore: "",
    ddt_previsto: "",
    data_arrivo_prevista: "",
    note: ""
  });
  
  const [ricezioneForm, setRicezioneForm] = useState({
    data_arrivo: "",
    ddt_arrivo: "",
    quantita_ricevuta: "",
    note_ricezione: "",
    area_id: "",
    posizione_x: 0,
    posizione_y: 0
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      let url = "/tenant/produzione/materiale";
      const params = new URLSearchParams();
      if (filtroCommessa && filtroCommessa !== "__all__") params.append("commessa_id", filtroCommessa);
      if (filtroStato === "ricevuto") params.append("ricevuto", "true");
      if (filtroStato === "attesa") params.append("ricevuto", "false");
      if (filtroStabilimento) params.append("stabilimento_id", filtroStabilimento);
      if (params.toString()) url += `?${params.toString()}`;

      const [matRes, commRes, stabRes] = await Promise.all([
        api.get(url),
        api.get("/tenant/produzione/commesse"),
        api.get("/tenant/produzione/stabilimenti")
      ]);
      
      setMateriali(matRes.data);
      setCommesse(commRes.data);
      setStabilimenti(stabRes.data);
    } catch (error) {
      
      handleApiError(error, t("errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [filtroCommessa, filtroStato, filtroStabilimento, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadAree = async (stabilimentoId) => {
    if (!stabilimentoId) {
      setAree([]);
      return;
    }
    try {
      const res = await api.get(`/tenant/produzione/stabilimenti/${stabilimentoId}/aree`);
      setAree(res.data);
    } catch (error) {
      
    }
  };

  const openCreateDialog = () => {
    setEditingMateriale(null);
    const activeCommessa = filtroCommessa !== "__all__" ? filtroCommessa : "";
    setFormData({
      commessa_id: activeCommessa,
      sottocommessa_id: "",
      codice_marca: "",
      descrizione: "",
      quantita: 1,
      unita_misura: "pz",
      peso_kg: "",
      dimensioni: "",
      fornitore: "",
      ddt_previsto: "",
      data_arrivo_prevista: "",
      note: ""
    });
    setShowDialog(true);
  };

  const openRicezioneDialog = async (materiale) => {
    setRicevendoMateriale(materiale);
    setRicezioneForm({
      data_arrivo: new Date().toISOString().split('T')[0],
      ddt_arrivo: "",
      quantita_ricevuta: materiale.quantita,
      note_ricezione: "",
      area_id: materiale.area_id || "__none__",
      posizione_x: materiale.posizione_x || 0,
      posizione_y: materiale.posizione_y || 0
    });
    
    // Carica aree dello stabilimento associato alla commessa
    const commessa = commesse.find(c => c.id === materiale.commessa_id);
    if (commessa?.stabilimento_id) {
      await loadAree(commessa.stabilimento_id);
    }
    
    setShowRicezioneDialog(true);
  };

  const handleSave = async () => {
    if (!formData.commessa_id || !formData.codice_marca || !formData.descrizione) {
      toast.error("Commessa, codice marca e descrizione sono obbligatori");
      return;
    }
    try {
      await api.post("/tenant/produzione/materiale", formData);
      toast.success("Materiale registrato");
      setShowDialog(false);
      loadData();
    } catch (error) {
      handleApiError(error, t("errors.saveFailed"));
    }
  };

  const handleRicezione = async () => {
    if (!ricezioneForm.data_arrivo) {
      toast.error("Data arrivo obbligatoria");
      return;
    }
    try {
      const dataToSend = {
        ricevuto: true,
        ...ricezioneForm,
        quantita_ricevuta: parseFloat(ricezioneForm.quantita_ricevuta) || ricevendoMateriale.quantita,
        area_id: ricezioneForm.area_id === "__none__" ? null : ricezioneForm.area_id
      };
      await api.put(`/tenant/produzione/materiale/${ricevendoMateriale.id}/ricezione`, dataToSend);
      toast.success("Ricezione registrata");
      setShowRicezioneDialog(false);
      loadData();
    } catch (error) {
      handleApiError(error, t("errors.errorRegistration"));
    }
  };

  const handleDelete = async (id) => {
    if (!confirm(t("confirm.deleteMaterial"))) return;
    try {
      await api.delete(`/tenant/produzione/materiale/${id}`);
      toast.success("Materiale eliminato");
      loadData();
    } catch (error) {
      handleApiError(error, t("errors.deleteFailed"));
    }
  };

  // Filtra per ricerca
  const materialiFiltrati = materiali.filter(m =>
    m.codice_marca.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.descrizione.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (m.fornitore && m.fornitore.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const materialiAttesa = materialiFiltrati.filter(m => !m.ricevuto);
  const materialiRicevuti = materialiFiltrati.filter(m => m.ricevuto);

  return (
    <div className="space-y-6" data-testid="produzione-materiale-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/${tenant}/produzione`)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{t("production.materials")}</h1>
            <p className="text-muted-foreground">Traccia materiale in arrivo e ricevuto</p>
          </div>
        </div>
        <Button onClick={openCreateDialog} data-testid="btn-nuovo-materiale">
          <Plus className="h-4 w-4 mr-2" />
          Registra Materiale Atteso
        </Button>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">In Attesa</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-yellow-600">{materialiAttesa.length}</div>
            <p className="text-xs text-muted-foreground">Materiali da ricevere</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Ricevuti</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">{materialiRicevuti.length}</div>
            <p className="text-xs text-muted-foreground">Materiali in magazzino</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">{t("common.total")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{materiali.length}</div>
            <p className="text-xs text-muted-foreground">Materiali tracciati</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtri */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("production.searchByCode")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filtroCommessa} onValueChange={setFiltroCommessa}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder={t("production.allOrders")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutte le commesse</SelectItem>
                {commesse.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.codice} - {c.titolo}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filtroStato} onValueChange={setFiltroStato}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder={t("common.allStatuses")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all_status__">Tutti gli stati</SelectItem>
                <SelectItem value="attesa">In attesa</SelectItem>
                <SelectItem value="ricevuto">Ricevuto</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="attesa" className="space-y-4">
        <TabsList>
          <TabsTrigger value="attesa" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            In Attesa ({materialiAttesa.length})
          </TabsTrigger>
          <TabsTrigger value="ricevuto" className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" />
            Ricevuti ({materialiRicevuti.length})
          </TabsTrigger>
        </TabsList>

        {/* Tab In Attesa */}
        <TabsContent value="attesa">
          <Card>
            <CardContent className="pt-4">
              {materialiAttesa.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="h-12 w-12 mx-auto opacity-50 mb-2" />
                  <p>{t("production.noPendingMaterial")}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Codice/Marca</TableHead>
                      <TableHead>{t("common.description")}</TableHead>
                      <TableHead>Commessa</TableHead>
                      <TableHead>Fornitore</TableHead>
                      <TableHead>DDT Previsto</TableHead>
                      <TableHead className="text-right">Quantita</TableHead>
                      <TableHead>Arrivo Previsto</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {materialiAttesa.map((mat) => (
                      <TableRow key={mat.id} data-testid={`materiale-${mat.id}`}>
                        <TableCell className="font-mono font-medium">{mat.codice_marca}</TableCell>
                        <TableCell>
                          <div>{mat.descrizione}</div>
                          {mat.dimensioni && (
                            <span className="text-xs text-muted-foreground">{mat.dimensioni}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{mat.commessa_codice}</Badge>
                          {mat.sottocommessa_codice && (
                            <Badge variant="secondary" className="ml-1 text-xs">
                              {mat.sottocommessa_codice}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>{mat.fornitore || "-"}</TableCell>
                        <TableCell>{mat.ddt_previsto || "-"}</TableCell>
                        <TableCell className="text-right">
                          {mat.quantita} {mat.unita_misura}
                          {mat.peso_kg && (
                            <div className="text-xs text-muted-foreground">{mat.peso_kg} kg</div>
                          )}
                        </TableCell>
                        <TableCell>
                          {mat.data_arrivo_prevista 
                            ? new Date(mat.data_arrivo_prevista).toLocaleDateString('it-IT')
                            : "-"}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button 
                              size="sm" 
                              onClick={() => openRicezioneDialog(mat)}
                              data-testid={`btn-ricevi-${mat.id}`}
                            >
                              <Download className="h-4 w-4 mr-1" />
                              Ricevi
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleDelete(mat.id)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Ricevuti */}
        <TabsContent value="ricevuto">
          <Card>
            <CardContent className="pt-4">
              {materialiRicevuti.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Boxes className="h-12 w-12 mx-auto opacity-50 mb-2" />
                  <p>{t("production.noReceivedMaterial")}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Codice/Marca</TableHead>
                      <TableHead>{t("common.description")}</TableHead>
                      <TableHead>Commessa</TableHead>
                      <TableHead>Fornitore</TableHead>
                      <TableHead>DDT Arrivo</TableHead>
                      <TableHead className="text-right">Quantita</TableHead>
                      <TableHead>Data Arrivo</TableHead>
                      <TableHead>Posizione</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {materialiRicevuti.map((mat) => (
                      <TableRow key={mat.id}>
                        <TableCell className="font-mono font-medium">{mat.codice_marca}</TableCell>
                        <TableCell>
                          <div>{mat.descrizione}</div>
                          {mat.dimensioni && (
                            <span className="text-xs text-muted-foreground">{mat.dimensioni}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{mat.commessa_codice}</Badge>
                        </TableCell>
                        <TableCell>{mat.fornitore || "-"}</TableCell>
                        <TableCell>{mat.ddt_arrivo || "-"}</TableCell>
                        <TableCell className="text-right">
                          {mat.quantita_ricevuta || mat.quantita} {mat.unita_misura}
                        </TableCell>
                        <TableCell>
                          {mat.data_arrivo 
                            ? new Date(mat.data_arrivo).toLocaleDateString('it-IT')
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {mat.area_nome ? (
                            <Badge variant="secondary" className="flex items-center gap-1 w-fit">
                              <MapPin className="h-3 w-3" />
                              {mat.area_nome}
                            </Badge>
                          ) : "-"}
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDelete(mat.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Nuovo Materiale */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("production.registerMaterial")}</DialogTitle>
            <DialogDescription>Inserisci i dati del materiale in arrivo</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label>Commessa *</Label>
              <Select 
                value={formData.commessa_id}
                onValueChange={(v) => setFormData({...formData, commessa_id: v, sottocommessa_id: ""})}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("common.selectPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {commesse.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.codice} - {c.titolo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Codice/Marca *</Label>
              <Input
                value={formData.codice_marca}
                onChange={(e) => setFormData({...formData, codice_marca: e.target.value})}
                placeholder="es. MARCA-001"
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Descrizione *</Label>
              <Textarea
                value={formData.descrizione}
                onChange={(e) => setFormData({...formData, descrizione: e.target.value})}
                placeholder="Descrizione dettagliata del materiale..."
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Quantita</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  value={formData.quantita}
                  onChange={(e) => setFormData({...formData, quantita: parseFloat(e.target.value) || 1})}
                  className="flex-1"
                />
                <Select 
                  value={formData.unita_misura}
                  onValueChange={(v) => setFormData({...formData, unita_misura: v})}
                >
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITA_MISURA.map(u => (
                      <SelectItem key={u} value={u}>{u}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Peso (kg)</Label>
              <Input
                type="number"
                value={formData.peso_kg}
                onChange={(e) => setFormData({...formData, peso_kg: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Dimensioni</Label>
              <Input
                value={formData.dimensioni}
                onChange={(e) => setFormData({...formData, dimensioni: e.target.value})}
                placeholder="es. 1000x500x10 mm"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('common.supplier')}</Label>
              <Input
                value={formData.fornitore}
                onChange={(e) => setFormData({...formData, fornitore: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>DDT Previsto</Label>
              <Input
                value={formData.ddt_previsto}
                onChange={(e) => setFormData({...formData, ddt_previsto: e.target.value})}
                placeholder="Numero DDT"
              />
            </div>
            <div className="space-y-2">
              <Label>Data Arrivo Prevista</Label>
              <Input
                type="date"
                value={formData.data_arrivo_prevista}
                onChange={(e) => setFormData({...formData, data_arrivo_prevista: e.target.value})}
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>{t("common.notes")}</Label>
              <Textarea
                value={formData.note}
                onChange={(e) => setFormData({...formData, note: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleSave}>Registra</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Ricezione */}
      <Dialog open={showRicezioneDialog} onOpenChange={setShowRicezioneDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("production.registerReceipt")}</DialogTitle>
            <DialogDescription>
              {ricevendoMateriale && (
                <span className="font-medium">{ricevendoMateriale.codice_marca} - {ricevendoMateriale.descrizione}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data Arrivo *</Label>
                <Input
                  type="date"
                  value={ricezioneForm.data_arrivo}
                  onChange={(e) => setRicezioneForm({...ricezioneForm, data_arrivo: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Quantita Ricevuta</Label>
                <Input
                  type="number"
                  value={ricezioneForm.quantita_ricevuta}
                  onChange={(e) => setRicezioneForm({...ricezioneForm, quantita_ricevuta: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>DDT Arrivo</Label>
              <Input
                value={ricezioneForm.ddt_arrivo}
                onChange={(e) => setRicezioneForm({...ricezioneForm, ddt_arrivo: e.target.value})}
                placeholder="Numero DDT di ricezione"
              />
            </div>
            <div className="space-y-2">
              <Label>Area di Stoccaggio</Label>
              <Select 
                value={ricezioneForm.area_id}
                onValueChange={(v) => setRicezioneForm({...ricezioneForm, area_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona area..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t("common.noArea")}</SelectItem>
                  {aree.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Note Ricezione</Label>
              <Textarea
                value={ricezioneForm.note_ricezione}
                onChange={(e) => setRicezioneForm({...ricezioneForm, note_ricezione: e.target.value})}
                placeholder="Note sulla ricezione..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRicezioneDialog(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleRicezione}>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Conferma Ricezione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
